import { Phone, MessageCircle, Mail, Globe, ArrowUp, ShieldCheck, Download } from "lucide-react";
import Logo from "@/components/Logo";
import { useI18n } from "@/lib/i18n";
import { API } from "@/lib/api";

const QUICK_LINKS = [
  { id: "customers", label: "For Customers" },
  { id: "services", label: "Loan Products" },
  { id: "corporate", label: "Corporate Services" },
  { id: "associates", label: "For Associates" },
  { id: "dsa-slabs", label: "DSA Payouts" },
  { id: "about", label: "About Us" },
  { id: "contact", label: "Contact" },
];

// One consolidated "Legal & Compliance" column — Policy, Privacy, T&C, Refund, Grievance.
const LEGAL_LINKS = [
  { href: "/policies", label: "Policy Hub" },
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/terms", label: "Terms & Conditions" },
  { href: "/refund", label: "Cancellation & Refund" },
  { href: "/grievance", label: "Grievance Redressal" },
];

// Company / resources — leadership, careers, CSR, blogs, etc.
const COMPANY_LINKS = [
  { href: "/governance", label: "Corporate Governance" },
  { href: "/leadership", label: "Our Leadership" },
  { href: "/careers", label: "Careers & Grades" },
  { href: "/csr", label: "CSR Initiatives" },
  { href: "/investors", label: "Investor Relations" },
  { href: "/customer-services", label: "Customer Services" },
  { href: "/my-letter", label: "My Eligibility Letter" },
  { href: "/track", label: "Track Application" },
  { href: "/hot-offers", label: "Hot Offers" },
  { href: "/blogs", label: "Insights / Blogs" },
];

// Standard L&D form templates — downloadable blank PDFs.
const TRAINING_FORMS = [
  { code: "SF-NI-LD-TAS-001", label: "Attendance Sheet (Part A)" },
  { code: "SF-NI-LD-ETF-001", label: "Evaluation Form (Part B)" },
  { code: "SF-NI-LD-ATC-001", label: "Advisor Certificate (Part C)" },
  { code: "SF-NI-LD-DTC-001", label: "DSA Certificate (Part D)" },
];

const SERVICES_LINKS = [
  "Personal Loan",
  "Business Loan",
  "Agriculture Loan",
  "Mortgage Loan",
  "Housing Loan",
  "Cattle Loan",
  "Loan against FD",
  "Loan against Credit Card",
  "Social-Credit Loan",
];

const REGISTRATIONS = [
  "CIN U66190GJ2025PTC171248",
  // LR-004 · Money-Lender chip TEMPORARILY HIDDEN pending renewal
  // certificate covering the period past 31/03/2026. Historical record
  // Tapi/56/2021 preserved in /app/backend/legal_registry.py; do NOT
  // restore on any surface until the renewal is verified.
  "UDYAM-GJ-24-0178730",
  "GSTIN 24AVFFP6564L2Z4",
  "PAN AALCN1559R",
  "TAN AHMN15110E",
];

export default function Footer() {
  const { t } = useI18n();
  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#04102B] text-white pt-20 pb-8 overflow-hidden noise-overlay"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/8 blur-3xl" />

      <div className="relative max-w-7xl mx-auto gutter">
        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-12 gap-x-8 lg:gap-x-10 gap-y-10 lg:gap-y-12">
          {/* Brand */}
          <div className="min-[420px]:col-span-2 lg:col-span-4 min-w-0">
            <div className="flex flex-wrap items-center gap-4 sm:gap-5">
              <Logo size={88} className="sm:!w-[112px] sm:!h-[112px] lg:!w-[132px] lg:!h-[132px]" testId="footer-logo-mark" />
              <div className="leading-tight min-w-0">
                <div className="font-display text-lg sm:text-xl md:text-2xl uppercase tracking-[0.01em] font-semibold leading-tight">
                  SAHYOG FINANCE<br />
                  <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
                </div>
                <div className="font-display text-[#D4AF37] text-[11px] md:text-sm tracking-[0.15em] uppercase mt-2">
                  Asset Management Pvt. Ltd.
                </div>
              </div>
            </div>
            <p className="mt-5 text-white/65 leading-relaxed text-sm max-w-sm">
              Noble Infinity Asset Management Pvt Ltd — operating the trusted{" "}
              <span className="text-white">Sahyog Finance</span> banner from Vyara, Tapi (Gujarat).
              Nine loan products, no CIBIL needed, ROI 6.5% to 15% p.a.
            </p>
            <p className="mt-5 font-script italic text-[#F0D788] text-lg leading-snug">
              તમારા વિશ્વાસની અમારી સાથે સહયાત્રા
            </p>
            <p className="text-white/55 text-xs">Your trust · Our service · Your success</p>
            <p className="mt-5 text-white/45 text-[11px] leading-relaxed">
              A-106, Ambaji Complex, Ambaji Mata Road,
              <br />
              Vyara, Tapi, Gujarat — 394650 · India
            </p>
          </div>

          {/* Explore (in-page navigation) */}
          <div className="lg:col-span-2 min-w-0">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">{t("footer.explore")}</h4>
            <ul className="mt-5 space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => handleNav(l.id)}
                    className="text-white/75 hover:text-[#F0D788] text-sm transition-colors text-left"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Compliance — consolidated column */}
          <div className="lg:col-span-2 min-w-0">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
              Legal &amp; Compliance
            </h4>
            <ul className="mt-5 space-y-3">
              {LEGAL_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    data-testid={`footer-legal-${l.href.replace(/\//g, "")}`}
                    href={l.href}
                    className="text-white/75 hover:text-[#F0D788] text-sm transition-colors text-left"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>

            <h4 className="mt-8 text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
              Company &amp; Resources
            </h4>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  data-testid="footer-company-training"
                  href="/training"
                  className="text-white/75 hover:text-[#F0D788] text-sm transition-colors text-left"
                >
                  Training Programs
                </a>
              </li>
              {COMPANY_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    data-testid={`footer-company-${l.href.replace(/\//g, "")}`}
                    href={l.href}
                    className="text-white/75 hover:text-[#F0D788] text-sm transition-colors text-left"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="lg:col-span-2 min-w-0">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">{t("footer.products")}</h4>
            <ul className="mt-5 space-y-3">
              {SERVICES_LINKS.map((s) => (
                <li key={s}>
                  <button
                    onClick={() => handleNav("services")}
                    className="text-white/75 hover:text-[#F0D788] text-sm transition-colors text-left"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>

            {/* Downloadable training materials */}
            <h4 className="mt-8 text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
              Training Downloads
            </h4>
            <ul data-testid="footer-training-downloads" className="mt-5 space-y-2.5">
              {TRAINING_FORMS.map((f) => (
                <li key={f.code}>
                  <a
                    data-testid={`footer-training-download-${f.code}`}
                    href={`${API}/training/forms/${f.code}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-start gap-2 text-white/75 hover:text-[#F0D788] text-sm transition-colors text-left"
                  >
                    <Download size={12} className="mt-1 shrink-0 text-[#D4AF37] group-hover:text-[#F0D788]" />
                    <span>{f.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2 min-w-0">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">Get in touch</h4>
            <ul className="mt-5 space-y-3 text-sm wrap-anywhere">
              <li>
                <a
                  data-testid="footer-phone"
                  href="tel:+917016975324"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <Phone size={14} className="text-[#D4AF37]" />
                  70169 75324
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-phone2"
                  href="tel:+919879630926"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <Phone size={14} className="text-[#D4AF37]" />
                  +91 98796 30926
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-phone3"
                  href="tel:+912626355386"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <Phone size={14} className="text-[#D4AF37]" />
                  02626-355386
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href="https://wa.me/917016975324"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <MessageCircle size={14} className="text-[#D4AF37]" />
                  WhatsApp 70169 75324
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email"
                  href="mailto:sahyogpfir@gmail.com"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <Mail size={14} className="text-[#D4AF37]" />
                  sahyogpfir@gmail.com
                </a>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5 ml-6">
                  Sahyog Finance · Loans
                </div>
              </li>
              <li>
                <a
                  data-testid="footer-email2"
                  href="mailto:info.nobleinfinity@gmail.com"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <Mail size={14} className="text-[#D4AF37]" />
                  info.nobleinfinity@gmail.com
                </a>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5 ml-6">
                  Noble Infinity · Investments
                </div>
              </li>
              <li>
                <a
                  data-testid="footer-web"
                  href="https://www.sahyogtapi.in"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-white/80 hover:text-[#F0D788] transition"
                >
                  <Globe size={14} className="text-[#D4AF37]" />
                  www.sahyogtapi.in
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Registrations strip */}
        <div
          data-testid="footer-registrations"
          className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]/80 shrink-0">
            <ShieldCheck size={14} />
            Verified Registrations
          </div>
          <div className="flex flex-wrap gap-2">
            {REGISTRATIONS.map((r) => (
              <span
                key={r}
                className="inline-block text-[10px] px-2.5 py-1 rounded-full border border-[#D4AF37]/25 bg-white/[0.03] text-white/65"
              >
                {r}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-white/50 text-xs leading-relaxed">
            © {year} Noble Infinity Asset Management Pvt Ltd · Sahyog Finance. {t("footer.rights")}
            Loans subject to credit policy &amp; terms.
          </p>
          <button
            data-testid="footer-back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/85 hover:text-[#F0D788] transition min-h-[44px] shrink-0"
          >
            Back to top <ArrowUp size={14} />
          </button>
        </div>

        <div className="mt-4 text-center md:text-right">
          <a
            data-testid="footer-admin-link"
            href="/admin"
            className="text-[10px] uppercase tracking-[0.25em] text-white/35 hover:text-[#D4AF37] transition"
          >
            · Staff Login ·
          </a>
        </div>
      </div>
    </footer>
  );
}
